// Hooks below use named function expressions (for stack traces); allow them past prefer-arrow-callback.

/* eslint-disable prefer-arrow-callback */
import { type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  productAnalytics,
  setStoredSoilSightProjectId,
  useCreateSoilSightProject,
  useCreateSoilSightScenario,
  useDeleteSoilSightScenario,
  useNavigate,
  useReplaceSoilSightCropYears,
  useTranslation,
  useUpdateSoilSightProjectSettingsRefinements,
} from "utils";

import { LIST_SOIL_SIGHT_PROJECTS } from "consts";

import type { DroppedMainCrop, SoilSightProject } from "models";

import { useFieldImportPersist } from "pages/app/scenario-analysis/hooks/useFieldImportPersist";
import { type CropYear } from "pages/app/scenario-analysis/types/scenario";
import { pruneEmptyCropYears } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { padCropYearsToMinimum } from "pages/app/scenario-analysis/utils/padSoilSightCropYears";
import { createSoilSightProjectWithDefaults, showSoilSightSaveFailed, toCropYearsPayload } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";
import { isFieldImportReadyForRecalculation } from "pages/app/scenario-analysis/utils/scenarioValidation";

type Args = {
  /** True when the page was opened from FCPO and persistence is deferred to the dialog. */
  hasFcpoFlowInitial: boolean;
  /** Whether the farm list query has resolved (gates showing the dialog). */
  listProjectsQuerySuccess: boolean;
  klimFarmId: string;
  importedFieldCropYearsRef: MutableRefObject<CropYear[] | null>;
  importedSelectedFieldIdRef: MutableRefObject<string | null>;
  fieldImportAlreadyPersistedRef: MutableRefObject<boolean>;
  farmFields: Array<{ id: string; size: number }>;
  getNextProjectName: () => string;
  switchProject: (farmId: string) => void;
  onProjectCreated: (farmId: string) => void;
  setShowTransitionModal: (show: boolean) => void;
  setAwaitingInitialCalc: (awaiting: boolean) => void;
  /** Set directly (not via router state) because the dialog flow persists on the page's existing instance. */
  setDroppedMainCrops: (crops: DroppedMainCrop[]) => void;
  /** Called when the dialog is closed without creating or adding to a project. */
  onDismiss: () => void;
};

export const useSoilSightProjectOrchestration = ({
  hasFcpoFlowInitial,
  listProjectsQuerySuccess,
  klimFarmId,
  importedFieldCropYearsRef,
  importedSelectedFieldIdRef,
  fieldImportAlreadyPersistedRef,
  farmFields,
  getNextProjectName,
  switchProject,
  onProjectCreated,
  setShowTransitionModal,
  setAwaitingInitialCalc,
  setDroppedMainCrops,
  onDismiss,
}: Args) => {
  const { t } = useTranslation("scenarioAnalysis");
  // Navigating carries the transition state across the page remount that follows project creation.
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [hasFcpoProjectEntryFlow] = useState(hasFcpoFlowInitial);
  const { persistFieldImport, locationRegion } = useFieldImportPersist();
  const { mutateAsync: createProjectAsync } = useCreateSoilSightProject();
  const { mutateAsync: updateRefinementsAsync } = useUpdateSoilSightProjectSettingsRefinements();
  const { mutateAsync: createScenarioAsync } = useCreateSoilSightScenario();
  const { mutateAsync: replaceCropYearsAsync } = useReplaceSoilSightCropYears();
  const { mutateAsync: deleteScenarioAsync } = useDeleteSoilSightScenario();
  // A second Enter or click before the dialog re-renders closed must not create a second project or scenario.
  const isEntryActionRunningRef = useRef(false);
  const [showProjectEntryDialog, setShowProjectEntryDialog] = useState(false);
  const projectEntryDialogShownRef = useRef(false);
  const addToExistingFlowRef = useRef(false);

  // Show the project-entry dialog once when arriving from FCPO with existing projects.
  useEffect(
    function showEntryDialogOnceWhenFcpoFlowReady() {
      if (!hasFcpoProjectEntryFlow) return;
      if (!listProjectsQuerySuccess) return;
      if (projectEntryDialogShownRef.current) return;
      projectEntryDialogShownRef.current = true;
      setShowProjectEntryDialog(true);
    },
    [hasFcpoProjectEntryFlow, listProjectsQuerySuccess]
  );

  const handleProjectEntryCreateNew = useCallback(
    async function handleProjectEntryCreateNew() {
      if (isEntryActionRunningRef.current) return;
      isEntryActionRunningRef.current = true;
      setShowProjectEntryDialog(false);
      const cropYears = importedFieldCropYearsRef.current;
      const nextName = getNextProjectName();
      let createdProjectId: string | null = null;
      let importSucceeded = false;
      let droppedMainCrops: DroppedMainCrop[] = [];
      // Reflects what actually landed in the DB (pruned/padded by persistSoilSightsFieldImport),
      // not the raw pre-persist input — readiness must be judged against the persisted rotation.
      let persistedCropYears = cropYears;
      try {
        if (cropYears?.length) {
          // FCPO dialog flow: create a brand-new project with the imported crop years as baseline.
          // Intercept setStoredSoilSightProjectId so we capture the project ID even if crop year import later throws.
          const result = await persistFieldImport({
            klimFarmId,
            fieldId: importedSelectedFieldIdRef.current ?? "",
            cropYears,
            name: nextName,
            getTargetProjectId: () => null, // force new project creation
            storeProjectId: (ssId) => {
              createdProjectId = ssId;
              setStoredSoilSightProjectId(ssId);
            },
          });
          createdProjectId = result.soilSightProjectId;
          droppedMainCrops = result.droppedMainCrops;
          persistedCropYears = result.cropYears;
          importSucceeded = true;
        } else {
          // No pending crop years: create an empty project on the imported field, else the largest one.
          await createSoilSightProjectWithDefaults({
            body: { farmId: klimFarmId, locationRegion, referenceName: t("scenarioBuilder.reference"), name: nextName },
            fields: farmFields,
            preferredFieldId: importedSelectedFieldIdRef.current,
            // Captured as soon as the project exists, so a failed field assignment still lands on it.
            create: async (body) => {
              const created = await createProjectAsync(body);
              createdProjectId = created.soilSightProjectId;
              return created;
            },
            assignField: (args) => updateRefinementsAsync({ ...args, klimFarmId }),
          });
        }
      } catch (createError) {
        // The import can fail after the project was created; createdProjectId is still set then.
        showSoilSightSaveFailed(t, createError);
      }
      isEntryActionRunningRef.current = false;
      if (!createdProjectId) {
        // Nothing was created: the dialog reopens with the import kept, so the user can retry or pick another option.
        setShowProjectEntryDialog(true);
        return;
      }
      const importReady = importSucceeded && !!persistedCropYears?.length && isFieldImportReadyForRecalculation(persistedCropYears);
      importedFieldCropYearsRef.current = null;
      fieldImportAlreadyPersistedRef.current = importSucceeded;
      if (cropYears?.length) {
        // The create only invalidates the list, so the cache can still miss the new project.
        const knownProjects = queryClient.getQueryData<SoilSightProject[]>([LIST_SOIL_SIGHT_PROJECTS, klimFarmId]) ?? [];
        const projectCount = knownProjects.some((project) => project.id === createdProjectId) ? knownProjects.length : knownProjects.length + 1;
        productAnalytics.track("soilsights:project_create", { creation_mode: "field_import", project_count: projectCount });
      }
      onProjectCreated(createdProjectId);
      if (importReady) setAwaitingInitialCalc(true);
      setDroppedMainCrops(droppedMainCrops);
      // Show the modal AFTER project creation. The soilSightProjectId change can cause the
      // layout to remount this page (via setNavigationCrumbs), which would reset useState.
      // Calling setShowTransitionModal handles the re-render case; the navigate carries
      // showFieldImportTransition in state so the modal reinitializes correctly on remount.
      setShowTransitionModal(true);
      navigate("/scenarios", {
        replace: true,
        state: {
          showFieldImportTransition: true,
          fieldImportAlreadyPersisted: importSucceeded,
          cropRotationLoaded: importSucceeded && !!cropYears?.length,
          importReadyForRecalc: importReady,
          droppedMainCrops,
        },
      });
    },
    [
      createProjectAsync,
      farmFields,
      fieldImportAlreadyPersistedRef,
      getNextProjectName,
      importedFieldCropYearsRef,
      importedSelectedFieldIdRef,
      klimFarmId,
      locationRegion,
      navigate,
      onProjectCreated,
      persistFieldImport,
      queryClient,
      setAwaitingInitialCalc,
      setDroppedMainCrops,
      setShowTransitionModal,
      t,
      updateRefinementsAsync,
    ]
  );

  const handleProjectEntryAddToExisting = useCallback(
    async function handleProjectEntryAddToExisting(farmId: string, scenarioName: string) {
      if (isEntryActionRunningRef.current) return;
      isEntryActionRunningRef.current = true;
      setShowProjectEntryDialog(false);
      const cropYears = padCropYearsToMinimum(pruneEmptyCropYears(importedFieldCropYearsRef.current ?? []));
      let persistSucceeded = false;
      let droppedMainCrops: DroppedMainCrop[] = [];
      let newScenarioId: string | null = null;
      try {
        // Create a new scenario in the selected project with the imported crop years, then switch, so hydration includes it.
        const created = await createScenarioAsync({
          soilSightProjectId: farmId,
          body: { name: scenarioName, duplicateFromBaseline: false },
        });
        newScenarioId = created?.scenarioId ?? null;
        if (newScenarioId) {
          const replaceResult = await replaceCropYearsAsync({ scenarioId: newScenarioId, body: toCropYearsPayload(cropYears) });
          droppedMainCrops = replaceResult?.droppedMainCrops ?? [];
        }
        persistSucceeded = true;
      } catch (saveError) {
        showSoilSightSaveFailed(t, saveError);
      }
      isEntryActionRunningRef.current = false;
      if (!persistSucceeded) {
        // An empty scenario left by a failed rotation save is removed (best effort); the dialog reopens with the import kept.
        if (newScenarioId) deleteScenarioAsync(newScenarioId).catch(() => {});
        setShowProjectEntryDialog(true);
        return;
      }
      importedFieldCropYearsRef.current = null;
      fieldImportAlreadyPersistedRef.current = true;
      // The user just added a rotation to an existing project and should review it before a run, so no auto-recalc.
      addToExistingFlowRef.current = true;
      switchProject(farmId);
      // As in create-new, state and navigate together keep the transition modal if the page remounts after the switch.
      setShowTransitionModal(true);
      setDroppedMainCrops(droppedMainCrops);
      navigate("/scenarios", {
        replace: true,
        state: {
          showFieldImportTransition: true,
          fieldImportAlreadyPersisted: true,
          cropRotationLoaded: cropYears.some((year) => Boolean(year.crop?.trim())),
          importReadyForRecalc: false,
          droppedMainCrops,
        },
      });
    },
    [
      createScenarioAsync,
      deleteScenarioAsync,
      fieldImportAlreadyPersistedRef,
      importedFieldCropYearsRef,
      navigate,
      replaceCropYearsAsync,
      setDroppedMainCrops,
      setShowTransitionModal,
      switchProject,
      t,
    ]
  );

  const handleProjectEntryClose = useCallback(
    function handleProjectEntryClose() {
      setShowProjectEntryDialog(false);
      // Dismissed without a choice: the import is dropped and the shown project lands normally.
      importedFieldCropYearsRef.current = null;
      onDismiss();
    },
    [importedFieldCropYearsRef, onDismiss]
  );

  return {
    showProjectEntryDialog,
    hasFcpoProjectEntryFlow,
    addToExistingFlowRef,
    isEntryActionRunningRef,
    handleProjectEntryCreateNew,
    handleProjectEntryAddToExisting,
    handleProjectEntryClose,
  };
};
